import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ValueAdapter } from '../value.adapter';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { sftpAuthTypes, SftpPrivateKeyType, SftpServerLinkedService, GlobalVariables as shellGlobalVariables } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SftpServerLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: shellGlobalVariables.enableSFTPV2 ? '*' : undefined,
            typeProperties: {
                host: '*',
                port: '*',
                skipHostKeyValidation: '*',
                hostKeyFingerprint: '*',
                authenticationType: '*',
                userName: '*',
                password: 'secret',
                privateKeyPath: '*',
                privateKeyContent: 'secret',
                passPhrase: 'secret',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class SftpLinkedServiceAdapter extends LinkedServiceAdapter<SftpServerLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => SftpServerLinkedService {
        return SftpServerLinkedService;
    }

    protected model(): IModel {
        return SftpServerLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SftpServerLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SftpServerLinkedService;
        properties.type = Metamodel.modelFor(SftpServerLinkedService).name;

        const password = linkedService.authenticationType === sftpAuthTypes.Basic || linkedService.authenticationType === sftpAuthTypes.MultiFactor
            ? parameterizablePasswordOrAKVtoSecretBase(linkedService, SftpServerLinkedService.PASSWORD, this.context)
            : undefined;
        let privateKeyPath: string;
        let privateKeyContent: string | Model.SecretBase;
        let passPhrase: string | Model.SecretBase;
        if (linkedService.authenticationType === sftpAuthTypes.SshPublicKey || linkedService.authenticationType === sftpAuthTypes.MultiFactor) {
            if (linkedService.privateKeyType === SftpPrivateKeyType.Path) {
                privateKeyPath = this.valueToResource(linkedService, SftpServerLinkedService.PRIVATE_KEY_PATH);
            } else {
                privateKeyContent = parameterizablePasswordOrAKVtoSecretBase(linkedService, SftpServerLinkedService.PRIVATE_KEY_CONTENT, this.context);
            }
            passPhrase = parameterizablePasswordOrAKVtoSecretBase(linkedService, SftpServerLinkedService.PASS_PHRASE, this.context);
        }
        if (linkedService.isOldResource) {
            properties.version = linkedService.version === driverVersions.v2 ? '2.0' : undefined;
        } else {
            properties.version = linkedService.version === driverVersions.v2 ? '2.0' : '1.0';
        }
        properties.typeProperties = {
            host: this.valueToResource(linkedService, SftpServerLinkedService.HOST),
            port: this.valueToResource(linkedService, SftpServerLinkedService.PORT),
            skipHostKeyValidation: linkedService.skipHostKeyValidation,
            hostKeyFingerprint: linkedService.skipHostKeyValidation ? undefined : this.valueToResource(linkedService, SftpServerLinkedService.HOST_KEY_FINGERPRINT),
            authenticationType: linkedService.authenticationType,
            userName: this.valueToResource(linkedService, SftpServerLinkedService.USER_NAME),
            password,
            privateKeyPath,
            privateKeyContent,
            passPhrase
        };

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SftpServerLinkedService): Promise<SftpServerLinkedService> {
        const properties = linkedServiceResource.properties as Model.SftpServerLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SftpServerLinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, SftpServerLinkedService.HOST, typeProperties.host);
        ValueAdapter.toModel(linkedService, SftpServerLinkedService.PORT, typeProperties.port);
        linkedService.skipHostKeyValidation = typeProperties.skipHostKeyValidation;
        linkedService.isOldResource = !properties.version;
        if (!!properties.version) {
            linkedService.version = properties.version === '2.0' ? driverVersions.v2 : driverVersions.v1;
        } else {
            linkedService.version = driverVersions.v1;
        }
        ValueAdapter.toModel(linkedService, SftpServerLinkedService.HOST_KEY_FINGERPRINT, typeProperties.hostKeyFingerprint);
        linkedService.authenticationType = typeProperties.authenticationType;
        ValueAdapter.toModel(linkedService, SftpServerLinkedService.USER_NAME, typeProperties.userName);
        parameterizableSecretBaseToModel(linkedService, SftpServerLinkedService.PASSWORD, typeProperties.password, this.context,
            linkedService.authenticationType === sftpAuthTypes.Basic || linkedService.authenticationType === sftpAuthTypes.MultiFactor);
        ValueAdapter.toModel(linkedService, SftpServerLinkedService.PRIVATE_KEY_PATH, typeProperties.privateKeyPath);
        if (linkedService.privateKeyPath || ValueAdapter.isTemplated(linkedService, SftpServerLinkedService.PRIVATE_KEY_PATH)) {
            linkedService.privateKeyType = SftpPrivateKeyType.Path;
            linkedService.isExistingResourceUsingPrivateKeyPath = true;
        }
        parameterizableSecretBaseToModel(linkedService, SftpServerLinkedService.PRIVATE_KEY_CONTENT, typeProperties.privateKeyContent, this.context,
            (linkedService.authenticationType === sftpAuthTypes.SshPublicKey || linkedService.authenticationType === sftpAuthTypes.MultiFactor)
            && linkedService.privateKeyType !== SftpPrivateKeyType.Path);
        parameterizableSecretBaseToModel(linkedService, SftpServerLinkedService.PASS_PHRASE, typeProperties.passPhrase, this.context,
            linkedService.authenticationType === sftpAuthTypes.SshPublicKey || linkedService.authenticationType === sftpAuthTypes.MultiFactor);

        return linkedService;
    }
}
